import { Elements, ExpressCheckoutElement, useElements } from '@stripe/react-stripe-js';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { resolveCheckoutGameName } from '@/utils/publisher';
import type {
  Stripe,
  StripeExpressCheckoutElementClickEvent,
  StripeExpressCheckoutElementConfirmEvent,
  StripeExpressCheckoutElementReadyEvent,
  StripeExpressCheckoutElementShippingAddressChangeEvent,
} from '@stripe/stripe-js';
import { APPLE_PAY_PAYMENT_CONFIG } from '@/constants/stripe';
import {
  failExpressCheckoutSheet,
  getStripeExpressCheckoutBusinessConfig,
  getStripeExpressCheckoutShippingConfig,
  isExpressPaymentFailure,
} from '@/utils/stripe';
import { useIsWalletZipTaxActive } from '@/hooks/useIsWalletZipTaxActive';
import { PaymentMethodEnum, ProviderEnum } from '@/types/common';
import { useTranslate } from '@/hooks/useTranslate';
import { useApplePayLoadTracking } from '@/hooks/useApplePayLoadTracking';
import { mixpanelService } from '@/services/mixpanelService';
import { MixpanelEvents } from '@/constants/mixpanelEvents';
import { getMixpanelProperties } from '@/utils/mixpanelProperties';
import { useStripeInitialize } from '@/hooks/useStripeInitialize';
import { useExpressWalletFallback } from '@/hooks/useExpressWalletFallback';
import { useStripeWalletShippingZipTax } from '@/hooks/useStripeWalletShippingZipTax';
import {
  useStableStripeExpressElementsOptions,
  useSyncStripeElementsAmount,
} from '@/hooks/useStableStripeExpressElements';
import {
  EXPRESS_WALLET_ECE_HANG_TIMEOUT_MS,
  EXPRESS_WALLET_ECE_HANG_TIMEOUT_REASON,
  useExpressReadyTimeout,
} from '@/hooks/useExpressReadyTimeout';
import { useExpressReveal } from '@/context/ExpressRevealContext';
import { getZipCodeErrorMessageKey } from '@/utils/zipCode';
import { notifyPaymentIntentClick, trackAndNotifyPaymentClick } from '@/utils/paymentClickEvents';
import { trackApplePaySlowLoad, type ApplePayLoadPhases } from '@/utils/conversionTracking';
import { armApplePayTabFallbackIfEligible } from '@/utils/applePayTabFallback';
import { isApplePayTabMode } from '@/utils/location';
import { logError } from '@/utils/logger';
import { WalletButtonShell, type WalletReadyState } from '@/components/Wallets/WalletButton/WalletButtonShell';
import { ApplePayButtonPlaceholder } from '@/components/Wallets/WalletButton/ApplePayButtonPlaceholder';

interface ApplePayStripeFormProps {
  /** Optional FTD/SINGLE callback to surface ZIP collection inline instead of a global error. */
  onInvalidTaxClick?: () => void;
}

interface ApplePayButtonUIProps {
  onReady: (event: StripeExpressCheckoutElementReadyEvent) => void;
  onConfirm: (event: StripeExpressCheckoutElementConfirmEvent) => void;
  onShippingAddressChange?: (event: StripeExpressCheckoutElementShippingAddressChangeEvent) => void;
  onCancel?: () => void;
  onLoadError: () => void;
  onInvalidTaxClick?: () => void;
}

const ApplePayButtonUI = ({
  onReady,
  onConfirm,
  onShippingAddressChange,
  onCancel,
  onLoadError,
  onInvalidTaxClick,
}: ApplePayButtonUIProps) => {
  const context = useContext(CheckoutContext);
  const {
    actions: { setError },
    payment: { isFormValid },
    customer,
    featureFlags,
    sessionStartedWithoutValidTax,
    publisher,
    boot,
  } = context;
  // Options carry `emailRequired` + `shippingAddressRequired` for US/CA
  // sessions where the wallet-tax pipeline is on, so we can recompute tax
  // in-sheet.
  const isWalletZipTaxActive = useIsWalletZipTaxActive();
  const { t } = useTranslate();
  // Country only — email keystrokes must not remount ExpressCheckoutElement.
  const taxCountry = customer.taxCountry ?? customer.geoLocator?.countryCode2 ?? '';
  // ACDEV-26742: display-only company name on the Apple Pay sheet (gated by
  // APPLE_PAY_COMPANY_NAME_LABEL inside the config builder). Stable per
  // session, so it never remounts the ECE.
  const businessConfig = getStripeExpressCheckoutBusinessConfig(
    resolveCheckoutGameName(boot?.gameName, publisher?.companyName || boot?.companyName),
    featureFlags
  );
  const hasBusinessBranding = !!businessConfig.business;

  const expressCheckoutOptions = useMemo(
    () =>
      isWalletZipTaxActive
        ? {
            ...APPLE_PAY_PAYMENT_CONFIG,
            // Stripe rejects `emailRequired` on `click.resolve()` once any
            // other collection param is on options — so when we layer
            // `shippingAddressRequired` in via
            // `getStripeExpressCheckoutShippingConfig` we MUST move
            // `emailRequired` to options too.
            emailRequired: true,
            ...getStripeExpressCheckoutShippingConfig(customer, featureFlags, sessionStartedWithoutValidTax),
            ...businessConfig,
          }
        : hasBusinessBranding
          ? {
              ...APPLE_PAY_PAYMENT_CONFIG,
              // Same Stripe constraint as the ZIP-tax branch above:
              // `businessConfig` puts collection params (`billingAddressRequired`)
              // on options, so `emailRequired` must move to options too —
              // `handleClick` resolves bare in this case.
              emailRequired: true,
              ...businessConfig,
            }
          : APPLE_PAY_PAYMENT_CONFIG,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- customer email must not remount ECE
    [isWalletZipTaxActive, taxCountry, featureFlags, sessionStartedWithoutValidTax, hasBusinessBranding]
  );

  const handleClick = (event: StripeExpressCheckoutElementClickEvent) => {
    if (isWalletZipTaxActive) {
      trackAndNotifyPaymentClick(context, { paymentMethodName: PaymentMethodEnum.APPLE_PAY });
      // `emailRequired` is on `expressCheckoutOptions` in this path (see above).
      event.resolve();
      return;
    }
    if (isFormValid) {
      trackAndNotifyPaymentClick(context, { paymentMethodName: PaymentMethodEnum.APPLE_PAY });
      // With business branding, collection params live on options (see
      // `expressCheckoutOptions`) — Stripe rejects `emailRequired` on
      // `resolve()` in that case.
      event.resolve(hasBusinessBranding ? undefined : { emailRequired: true });
    } else {
      if (onInvalidTaxClick) {
        onInvalidTaxClick();
      } else {
        const errorMessageKey = getZipCodeErrorMessageKey(customer);
        setError(t(errorMessageKey));
      }
      event.reject();
    }
  };

  return (
    <ExpressCheckoutElement
      onClick={handleClick}
      onShippingAddressChange={onShippingAddressChange}
      onConfirm={onConfirm}
      onCancel={onCancel}
      onReady={onReady}
      onLoadError={onLoadError}
      options={expressCheckoutOptions}
    />
  );
};

interface ApplePayButtonProps {
  /** The instance behind the enclosing `<Elements>` — the payment must run on it. */
  stripe: Stripe;
  onReady: () => void;
  onUnavailable: (reason: string) => void;
  onInvalidTaxClick?: () => void;
}

const ApplePayButton = ({ stripe, onReady, onUnavailable, onInvalidTaxClick }: ApplePayButtonProps) => {
  const context = useContext(CheckoutContext);
  const {
    actions: { runPayment, setError },
    template,
    pricing,
  } = context;
  const elements = useElements();
  useSyncStripeElementsAmount(elements, pricing.priceBreakdown.priceTotal);
  // ACDEV-25067: gated internally on `WALLET_ZIP_TAX` — when FF-OFF, the hook
  // returns `onShippingAddressChange: undefined` and `capturedWalletTaxRef.current`
  // stays null, so wiring these props unconditionally is a functional no-op
  // for the pre-branch path.
  const {
    onShippingAddressChange: handleShippingAddressChange,
    capturedWalletTaxRef,
    applyWalletTaxToCheckout,
    logConfirmTaxState,
  } = useStripeWalletShippingZipTax(elements);
  const compactExpressForms = template.features.compactExpressForms;
  const { t } = useTranslate();

  const handleLoadError = (error?: unknown) => {
    logError('Apple Pay load error', error);
    // Inside the fallback tab a dead element ends the flow, and the answer is
    // the standing notice under the order total, which `onUnavailable` latches
    // on its way through. The compact branch below is wrong there twice over:
    // its toast fades, leaving the tab silent again, and skipping
    // `onUnavailable` would leave the placeholder spinning beside a message
    // saying the method is unavailable.
    if (error && compactExpressForms && !isApplePayTabMode()) {
      setError(t('paymentMethodNotAvailable'));
      return;
    }
    onUnavailable('load_error');
  };

  /**
   * Stripe ECE fires `onReady` even when the configured wallet is
   * unavailable on this device — it just leaves the button unpainted.
   * Per Stripe's type:
   *   availablePaymentMethods: undefined | AvailablePaymentMethods
   *   "...undefined if no payment methods can show."
   *
   * So unavailable manifests as EITHER:
   *   - `availablePaymentMethods === undefined`, OR
   *   - `availablePaymentMethods.applePay === false`
   *
   * Without this check we'd mark the form as ready, never call
   * `useExpressWalletFallback`, and strand the FTD / last-method
   * PREFERRED_PAYMENT_METHOD user on an invisible primary CTA.
   *
   * (`onLoadError` only fires for SDK-load failures, not for the
   * "device doesn't support Apple Pay" case.)
   */
  const handleReady = (event: StripeExpressCheckoutElementReadyEvent) => {
    const applePayAvailable = event.availablePaymentMethods?.applePay === true;
    if (!applePayAvailable) {
      onUnavailable('device_unsupported');
      return;
    }
    onReady();
  };

  const handleConfirm = async (event: StripeExpressCheckoutElementConfirmEvent) => {
    try {
      notifyPaymentIntentClick(context, { paymentMethodName: PaymentMethodEnum.APPLE_PAY });
      logConfirmTaxState(event);
      // `capturedWalletTaxRef.current?.zipCode` is undefined on the pre-branch path
      // (FF-OFF); the payment handler drops undefined zipCode, so the wire
      // format matches pre-branch when the FF is OFF.
      const result = await runPayment(
        { event, elements: elements!, stripe, zipCode: capturedWalletTaxRef.current?.zipCode },
        PaymentMethodEnum.APPLE_PAY
      );
      // `runPayment` surfaces its own errors and resolves `undefined`; the
      // Apple Pay sheet still needs to hear about the failure or it spins
      // until Apple's timeout.
      if (isExpressPaymentFailure(result)) {
        failExpressCheckoutSheet(event);
      }
    } catch (error: unknown) {
      failExpressCheckoutSheet(event);
      applyWalletTaxToCheckout();
      logError('Apple Pay confirm error', error);
    }
  };

  return (
    <ApplePayButtonUI
      onReady={handleReady}
      onConfirm={handleConfirm}
      onShippingAddressChange={handleShippingAddressChange}
      onCancel={applyWalletTaxToCheckout}
      onLoadError={handleLoadError}
      onInvalidTaxClick={onInvalidTaxClick}
    />
  );
};

export const ApplePayStripeForm = ({ onInvalidTaxClick }: ApplePayStripeFormProps) => {
  const context = useContext(CheckoutContext);
  const {
    pricing,
    actions: { setError },
    template,
    featureFlags,
  } = context;
  const compactExpressForms = template.features.compactExpressForms;
  // PREFERRED defers the whole-screen reveal until Apple Pay is confirmed (the
  // host view shows a skeleton meanwhile). Here it just arms the readiness
  // timeout so a wallet that never resolves still falls back to DEFAULT.
  const deferReveal = template.features.deferExpressWalletReveal;
  const { t } = useTranslate();
  const [state, setState] = useState<WalletReadyState>('loading');
  // Bridges Stripe ECE's `onLoadError` to the active template so the
  // FTD / last-method PREFERRED_PAYMENT_METHOD branches can swap to
  // DEFAULT when Apple Pay was advertised by the BE but Stripe's
  // merchant validation / device probe rejects it at runtime.
  const reportWalletAvailability = useExpressWalletFallback();
  // When a host view (PREFERRED) is gating the whole screen behind a skeleton,
  // tell it to reveal once Apple Pay is confirmed available.
  const reveal = useExpressReveal();
  // Anchored at this form's mount; closed below when Apple Pay is ready.
  const trackApplePayReady = useApplePayLoadTracking(ProviderEnum.STRIPE);
  const elementsOptions = useStableStripeExpressElementsOptions(
    pricing.priceBreakdown.priceTotal,
    pricing.currencyCode
  );

  // Slow-load stopwatch. `stripe_load` = form mount → Stripe.js ready;
  // `ece_probe` = Stripe ready → ECE onReady/onLoadError (the cross-origin
  // Apple Pay availability + merchant validation we can't see into directly).
  const mountTimeRef = useRef(Date.now());
  const stripeReadyAtRef = useRef<number | null>(null);
  const stripePhases = (now: number): ApplePayLoadPhases => {
    const stripeReadyAt = stripeReadyAtRef.current ?? now;
    return { stripe_load: stripeReadyAt - mountTimeRef.current, ece_probe: now - stripeReadyAt };
  };

  const handleReady = () => {
    const now = Date.now();
    trackApplePaySlowLoad(context, {
      payment_provider: ProviderEnum.STRIPE,
      phases: stripePhases(now),
      total_ms: now - mountTimeRef.current,
      outcome: 'ready',
    });
    trackApplePayReady();
    reportWalletAvailability(true, {
      wallet: 'apple_pay',
      provider: ProviderEnum.STRIPE,
      loadTimeMs: now - mountTimeRef.current,
    });
    reveal?.onWalletReady();
    setState('ready');
  };
  const handleUnavailable = (reason: string, timeoutMs?: number) => {
    const now = Date.now();
    trackApplePaySlowLoad(context, {
      payment_provider: ProviderEnum.STRIPE,
      phases: stripePhases(now),
      total_ms: now - mountTimeRef.current,
      outcome: 'unavailable',
    });
    // Embedding domain not Apple-Pay capable (common ECE signal): swap to the
    // fake new-tab button instead of collapsing / template-swapping away.
    // Parent `ApplePayForm` re-renders `ApplePayTabFallback`, which reports ready.
    if (armApplePayTabFallbackIfEligible(featureFlags)) return;
    reportWalletAvailability(false, {
      wallet: 'apple_pay',
      provider: ProviderEnum.STRIPE,
      reason,
      loadTimeMs: now - mountTimeRef.current,
      timeoutMs,
    });
    setState('unavailable');
  };

  // Safety net for the deferred-reveal path: if ECE never fires `onReady`/
  // `onLoadError` (hung SDK load, blocked iframe), don't strand the player on
  // a permanent skeleton — treat the silence as unavailable and fall back.
  useExpressReadyTimeout(deferReveal && state === 'loading', () => handleUnavailable('timeout'));

  const { stripe, isLoading: isStripeLoading } = useStripeInitialize({
    paymentMethod: PaymentMethodEnum.APPLE_PAY,
    isExpress: true,
  });

  useEffect(() => {
    if (stripe && !isStripeLoading && stripeReadyAtRef.current === null) {
      stripeReadyAtRef.current = Date.now();
    }
  }, [stripe, isStripeLoading]);

  // ECE mounted but silent, on every template rather than just the gated ones
  // above: the new-tab fallback is armed from `handleUnavailable`, so without a
  // breaker here a framed iOS phone whose domain really is unregistered would
  // sit on the placeholder forever instead of reaching the fake button. Armed
  // only once Stripe.js is ready so a cold SDK load isn't charged against this
  // budget, and the reason marks it as OUR hang-breaker — Stripe emits nothing
  // for silence.
  useExpressReadyTimeout(
    state === 'loading' && Boolean(stripe) && !isStripeLoading,
    () => handleUnavailable(EXPRESS_WALLET_ECE_HANG_TIMEOUT_REASON, EXPRESS_WALLET_ECE_HANG_TIMEOUT_MS),
    EXPRESS_WALLET_ECE_HANG_TIMEOUT_MS
  );

  try {
    return (
      <WalletButtonShell
        state={state}
        placeholder={<ApplePayButtonPlaceholder />}
        testId="wallet-btn-stripe-apple-tile"
        usePlaceholderAsVisual
      >
        {stripe && !isStripeLoading && (
          <Elements stripe={stripe} options={elementsOptions}>
            <ApplePayButton
              stripe={stripe}
              onReady={handleReady}
              onUnavailable={handleUnavailable}
              onInvalidTaxClick={onInvalidTaxClick}
            />
          </Elements>
        )}
      </WalletButtonShell>
    );
  } catch (error) {
    logError('Apple Pay Stripe Elements initialization error', error);
    if (compactExpressForms) {
      setError(t('paymentMethodNotAvailable'));
      mixpanelService.track(
        MixpanelEvents.PAYMENT_NOT_SUPPORTED,
        getMixpanelProperties(context) as Record<string, unknown>
      );
    }
    return null;
  }
};
